import { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { createLogger } from "../utils/logger";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const log = createLogger({ component: "ErrorBoundary" });

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error("Uncaught error in component tree", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <TriangleAlert className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-700 dark:text-dark-text mb-4">
            Something went wrong
          </h1>
          <p className="text-gray-500 dark:text-dark-text-secondary mb-8">
            An unexpected error occurred. Please try again.
          </p>
          <div
            className="flex items-center justify-center gap-4"
            role="group"
            aria-label="Error recovery options"
          >
            <button
              onClick={this.handleReset}
              className="inline-block bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-dark-bg"
            >
              Try Again
            </button>
            <Link
              to="/apps"
              onClick={this.handleReset}
              className="inline-block bg-gray-200 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary px-6 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-dark-border transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-dark-border focus:ring-offset-2 dark:focus:ring-offset-dark-bg"
            >
              Go Home
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
